import cn from 'classnames';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DatePicker } from '@webapp/common/components/date-picker';
import { ProPlusMark } from '@webapp/common/components/misc';
import { Select } from '@webapp/common/components/select';
import { Textfield, TextfieldType } from '@webapp/common/components/textfield';
import { Tooltip } from '@webapp/common/components/tooltip';
import { DATE_FORMAT, dateFormat2IMask, DateRange, PERIOD_OPTIONS, PICKER_DATE_FORMAT } from '@webapp/common/lib/date';

import css from './date-range-filter.css';

interface DatesRange {
    from: dayjs.Dayjs;
    to: dayjs.Dayjs;
}

const CUSTOM_PERIOD = 'customPeriod';

const today = (): DatesRange => {
    const d = dayjs();

    return {
        from: d,
        to: d,
    };
};

const allTime = (minDate: string | Date): DatesRange => {
    const to = dayjs();

    return { from: dayjs(minDate), to };
};

const yesterday = (): DatesRange => {
    const d = dayjs().subtract(1, 'day');

    return {
        from: d,
        to: d,
    };
};

const week = (): DatesRange => {
    const d = dayjs();

    return {
        from: d.subtract(7, 'day'),
        to: d,
    };
};

const month = (): DatesRange => {
    const d = dayjs();

    return {
        from: d.subtract(1, 'month'),
        to: d,
    };
};

export const DateRangeFilter: FC<{
    from: string;
    to: string;
    onChange: (filters: { from: string; to: string }) => void;
    className?: string;
    minDate: string;
    onComparePeriods?: () => void;
    onCompareDisable?: () => void;
    compareGated?: boolean;
    comparePeriodsCount?: number;
}> = ({
    className,
    compareGated,
    comparePeriodsCount = 0,
    from: fromDefault,
    minDate,
    onChange,
    onComparePeriods,
    to: toDefault,
}) => {
    // Режим сравнения активен, пока в фильтрах есть периоды сравнения.
    const compareActive = comparePeriodsCount > 0;
    const [period, setPeriod] = useState<DateRange | typeof CUSTOM_PERIOD>(DateRange.ALL_TIME);
    // Тултип для disabled-полей: события с disabled-контролов не приходят,
    // поэтому data-tooltip-id вешаем на обёртку.
    const [tooltipId] = useState(() => nanoid());
    const options = useMemo(() => [...PERIOD_OPTIONS, { label: 'Произвольный период', value: CUSTOM_PERIOD }], []);

    const applyPeriod = useCallback(
        (value) => {
            let { from, to } = today();

            switch (value) {
                case DateRange.ALL_TIME:
                    ({ from, to } = allTime(minDate));
                    break;
                case DateRange.TODAY:
                    // ({ from, to } = today());
                    break;
                case DateRange.YESTERDAY:
                    ({ from, to } = yesterday());
                    break;
                case DateRange.WEEK:
                    ({ from, to } = week());
                    break;
                case DateRange.MONTH:
                    ({ from, to } = month());
                    break;
            }
            onChange({
                from: from.format(DATE_FORMAT),
                to: to.format(DATE_FORMAT),
            });
        },
        [onChange, minDate],
    );

    const handleChangePeriod = useCallback(
        (value) => {
            if (compareActive || value === CUSTOM_PERIOD) {
                return;
            }

            applyPeriod(value);
        },
        [applyPeriod, compareActive],
    );

    const from = useMemo(() => dayjs(fromDefault), [fromDefault]);
    const to = useMemo(() => dayjs(toDefault), [toDefault]);

    useEffect(() => {
        if (!from.isValid() || !to.isValid()) {
            applyPeriod(DateRange.ALL_TIME);
        } else {
            const a = allTime(minDate);
            const t = today();
            const y = yesterday();
            const w = week();
            const m = month();

            if (t.from.isSame(from, 'day') && t.to.isSame(to, 'day')) {
                applyPeriod(DateRange.TODAY);
                setPeriod(DateRange.TODAY);
            } else if (y.from.isSame(from, 'day') && y.to.isSame(to, 'day')) {
                applyPeriod(DateRange.YESTERDAY);
                setPeriod(DateRange.YESTERDAY);
            } else if (w.from.isSame(from, 'day') && w.to.isSame(to, 'day')) {
                applyPeriod(DateRange.WEEK);
                setPeriod(DateRange.WEEK);
            } else if (m.from.isSame(from, 'day') && m.to.isSame(to, 'day')) {
                applyPeriod(DateRange.MONTH);
                setPeriod(DateRange.MONTH);
            } else if (a.from.isSame(from, 'day')) {
                // dateTo мог остаться вчерашним — режим всё равно «За всё время».
                setPeriod(DateRange.ALL_TIME);
            } else {
                setPeriod(CUSTOM_PERIOD);
            }
        }
    }, [from, to, applyPeriod, minDate]);

    return (
        <div className={cn(css.filters, className)}>
            <div
                className={cn(css.filter, css.selector, { [css.disabledField]: compareActive })}
                data-tooltip-id={compareActive ? tooltipId : undefined}
            >
                <Select disabled={compareActive} options={options} value={period} onChange={handleChangePeriod} />
            </div>
            <div
                className={cn(css.filter, css.date, { [css.disabledField]: compareActive })}
                data-tooltip-id={compareActive ? tooltipId : undefined}
            >
                <DatePicker
                    customInput={
                        <Textfield
                            disabled={compareActive}
                            mask={dateFormat2IMask(PICKER_DATE_FORMAT)}
                            placeholder={PICKER_DATE_FORMAT}
                            type={TextfieldType.DATE}
                        />
                    }
                    dateFormat={PICKER_DATE_FORMAT}
                    disabled={compareActive}
                    minDate={dayjs(minDate)}
                    selected={from.isValid() ? from.toDate() : undefined}
                    onChange={(fromDate) => {
                        onChange({ from: dayjs(+fromDate).format(DATE_FORMAT), to: to.format(DATE_FORMAT) });
                    }}
                />
            </div>
            <div
                className={cn(css.filter, css.date, { [css.disabledField]: compareActive })}
                data-tooltip-id={compareActive ? tooltipId : undefined}
            >
                <DatePicker
                    customInput={
                        <Textfield
                            disabled={compareActive}
                            mask={dateFormat2IMask(PICKER_DATE_FORMAT)}
                            placeholder={PICKER_DATE_FORMAT}
                            type={TextfieldType.DATE}
                        />
                    }
                    dateFormat={PICKER_DATE_FORMAT}
                    disabled={compareActive}
                    minDate={from}
                    selected={to.isValid() ? to.toDate() : undefined}
                    onChange={(toDate) => {
                        onChange({ from: from.format(DATE_FORMAT), to: dayjs(+toDate).format(DATE_FORMAT) });
                    }}
                />
            </div>
            {onComparePeriods && (
                <button
                    type='button'
                    className={cn(css.compareBtn, { [css.compareBtnGated]: compareGated })}
                    disabled={compareGated}
                    onClick={compareGated ? undefined : onComparePeriods}
                >
                    Сравнить периоды{compareActive && !compareGated ? ` (${comparePeriodsCount})` : ''}
                    {compareGated && <ProPlusMark label='(PRO+)' />}
                </button>
            )}
            {compareActive && <Tooltip id={tooltipId}>Чтобы выбрать дату, очистите периоды сравнения</Tooltip>}
        </div>
    );
};
